import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { X, FileSpreadsheet, Printer, Download, Settings } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerTurkishFont } from "@/lib/pdfFont";

async function loadLogoDataUrl() {
  try {
    const res = await fetch("/plasper-logo.jpg");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

export default function ReconciliationModal({ contact, onClose }) {
  const [asOf, setAsOf] = useState(todayISO());
  const [start, setStart] = useState(""); // opsiyonel dönem başlangıcı
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const printableRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `/contacts/${contact.id}/reconciliation?as_of=${asOf}` + (start ? `&start=${start}` : "");
      const { data } = await api.get(url);
      setData(data);
    } finally { setLoading(false); }
  }, [contact.id, asOf, start]);

  useEffect(() => { load(); }, [load]);

  const positionLabel = data && (
    data.our_position === "receivable" ? { text: `${contact.name} tarafından bize ödenecek`, tone: "text-emerald-700 border-emerald-300 bg-emerald-50" } :
    data.our_position === "payable" ? { text: `${contact.name} tarafına borçluyuz`, tone: "text-rose-700 border-rose-300 bg-rose-50" } :
    { text: "Kayıtlarımıza göre bakiyeler eşit — mutabık", tone: "text-neutral-700 border-neutral-300 bg-neutral-50" }
  );

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    // Ana mutabakat sayfası
    const meta = [
      ["PLASPER FİNANS - CARİ MUTABAKAT FORMU"],
      [""],
      ["Referans No", data.reference_no],
      ["Mutabakat Tarihi", data.as_of],
      ["Dönem Başlangıcı", data.period_start || "—"],
      [""],
      ["FİRMA BİLGİLERİ"],
      ["Ünvan", data.company.name],
      ["Vergi Dairesi / VKN", `${data.company.tax_office || "—"} / ${data.company.tax_id || "—"}`],
      ["Adres", data.company.address || "—"],
      ["Telefon", data.company.phone || "—"],
      ["E-posta", data.company.email || "—"],
      [""],
      ["CARİ BİLGİLERİ"],
      ["Ünvan", contact.name],
      ["VKN/TCKN", contact.tax_id || "—"],
      ["Adres", contact.address || "—"],
      ["Telefon", contact.phone || "—"],
      ["E-posta", contact.email || "—"],
      [""],
      ["BAKİYE ÖZETİ (Kayıtlarımıza göre)"],
      ["Dönem Başı Bakiye", data.opening_balance],
      ["Toplam Borç (+)", data.total_income],
      ["Toplam Alacak (−)", data.total_expense],
      ["Açık Satış Faturaları — Borç (+)", data.open_invoice_receivable || 0],
      ["  Kalan (kısmi ödemeler düşülmüş)", data.open_invoice_receivable_remaining ?? data.open_invoice_receivable ?? 0],
      ["Açık Alış Faturaları — Alacak (−)", data.open_invoice_payable || 0],
      ["  Kalan (kısmi ödemeler düşülmüş)", data.open_invoice_payable_remaining ?? data.open_invoice_payable ?? 0],
      ["Portföydeki Alınan Çekler — Alacak (−)", data.open_cheque_receivable || 0],
      ["Portföydeki Verilen Çekler — Borç (+)", data.open_cheque_payable || 0],
      ["Ciro ile Ödenen Çekler — Borç (+)", data.open_cheque_endorsed_in || 0],
      ["Mutabakat Tarihi İtibariyle Bakiye", data.closing_balance],
      ["Konum", positionLabel.text],
    ];
    const wsMeta = XLSX.utils.aoa_to_sheet(meta);
    wsMeta["!cols"] = [{ wch: 30 }, { wch: 50 }];
    XLSX.utils.book_append_sheet(wb, wsMeta, "Mutabakat");

    // Hareketler sayfası
    if (data.transactions.length) {
      const rows = data.transactions.map((t) => ({
        Tarih: t.date,
        Açıklama: t.description || "",
        Kategori: t.category || "",
        "Borç (+)": t.income || "",
        "Alacak (−)": t.expense || "",
      }));
      const ws2 = XLSX.utils.json_to_sheet(rows);
      ws2["!cols"] = [{ wch: 12 }, { wch: 40 }, { wch: 16 }, { wch: 14 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Hareketler");
    }

    const safe = contact.name.replace(/[^a-zA-Z0-9-_ğüşıöçİĞÜŞÖÇ]/g, "_");
    XLSX.writeFile(wb, `mutabakat_${safe}_${data.as_of}.xlsx`);
    toast.success("Excel indirildi");
  };

  const exportPDF = async () => {
    if (!data) return;
    const doc = new jsPDF({ compress: true });
    await registerTurkishFont(doc);
    const logo = await loadLogoDataUrl();
    const c = data.company;

    if (logo) {
      try { doc.addImage(logo, "JPEG", 14, 12, 45, 15, undefined, "FAST"); } catch {}
    }
    doc.setFontSize(16); doc.text("CARİ MUTABAKAT FORMU", 65, 20);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`Referans No: ${data.reference_no}`, 65, 27);
    doc.text(`Mutabakat Tarihi: ${data.as_of}`, 65, 32);
    doc.setTextColor(0);

    // Firma - Cari iki sütun
    doc.setFontSize(9);
    doc.setFont(undefined, "bold"); doc.text("FİRMAMIZ", 14, 46);
    doc.setFont(undefined, "normal");
    let y = 51;
    [c.name, c.tax_office && `V.D.: ${c.tax_office}`, c.tax_id && `VKN: ${c.tax_id}`,
     c.address, c.phone, c.email].filter(Boolean).forEach((line) => {
      doc.text(String(line).slice(0, 55), 14, y); y += 4.5;
    });

    doc.setFont(undefined, "bold"); doc.text("CARİ", 110, 46);
    doc.setFont(undefined, "normal");
    let yc = 51;
    [contact.name, contact.tax_id && `VKN: ${contact.tax_id}`,
     contact.address, contact.phone, contact.email].filter(Boolean).forEach((line) => {
      doc.text(String(line).slice(0, 55), 110, yc); yc += 4.5;
    });

    const startY = Math.max(y, yc) + 6;

    // Bakiye özeti tablosu
    autoTable(doc, {
      startY,
      head: [["Kayıtlarımıza Göre Bakiye Özeti", "Tutar (₺)"]],
      body: [
        ["Dönem Başı Bakiye", fmtTRY(data.opening_balance)],
        ["Toplam Borç (+)", fmtTRY(data.total_income)],
        ["Toplam Alacak (−)", fmtTRY(data.total_expense)],
        ...(data.open_invoice_receivable ? [["Açık Satış Faturaları — Borç (+)", fmtTRY(data.open_invoice_receivable)]] : []),
        ...(data.open_invoice_receivable_remaining != null && data.open_invoice_receivable_remaining !== data.open_invoice_receivable
          ? [["  Kalan (kısmi ödeme sonrası)", fmtTRY(data.open_invoice_receivable_remaining)]] : []),
        ...(data.open_invoice_payable ? [["Açık Alış Faturaları — Alacak (−)", fmtTRY(data.open_invoice_payable)]] : []),
        ...(data.open_invoice_payable_remaining != null && data.open_invoice_payable_remaining !== data.open_invoice_payable
          ? [["  Kalan (kısmi ödeme sonrası)", fmtTRY(data.open_invoice_payable_remaining)]] : []),
        ...(data.open_cheque_receivable ? [[`Portföydeki Alınan Çekler (${data.open_cheque_count || 0} adet, Alacak −)`, fmtTRY(data.open_cheque_receivable)]] : []),
        ...(data.open_cheque_payable ? [["Portföydeki Verilen Çekler — Borç (+)", fmtTRY(data.open_cheque_payable)]] : []),
        ...(data.open_cheque_endorsed_in ? [[`Ciro ile Ödenen Çekler (${data.open_cheque_endorsed_in_count || 0} adet, Borç +)`, fmtTRY(data.open_cheque_endorsed_in)]] : []),
        [{ content: `${data.as_of} İtibariyle Bakiye`, styles: { fontStyle: "bold" } },
         { content: fmtTRY(data.closing_balance), styles: { fontStyle: "bold" } }],
      ],
      styles: { fontSize: 10, font: "Roboto" },
      headStyles: { fillColor: [10, 10, 10], font: "Roboto", fontStyle: "bold" },
    });

    const summaryY = doc.lastAutoTable.finalY + 4;
    doc.setFontSize(10); doc.setFont(undefined, "bold");
    doc.text(positionLabel.text, 14, summaryY + 4);
    doc.setFont(undefined, "normal");

    // Hareketler (varsa)
    if (data.transactions.length) {
      autoTable(doc, {
        startY: summaryY + 10,
        head: [["Tarih", "Açıklama", "Kategori", "Borç (+)", "Alacak (−)"]],
        body: data.transactions.map((t) => [
          t.date, t.description || "-", t.category || "-",
          t.income ? fmtTRY(t.income) : "-",
          t.expense ? fmtTRY(t.expense) : "-",
        ]),
        styles: { fontSize: 8, font: "Roboto" },
        headStyles: { fillColor: [40, 40, 40], font: "Roboto", fontStyle: "bold" },
      });
    }

    // Onay bölümü
    const finalY = doc.lastAutoTable.finalY + 12;
    doc.setFontSize(10);
    doc.text("Kayıtlarınızla mutabık iseniz kaşe ve imzalayarak tarafımıza gönderiniz.", 14, finalY);
    doc.text("Mutabık değilseniz, farkları bildiren mutabakatsızlık formu ile geri dönmenizi rica ederiz.", 14, finalY + 5);

    // İki imza kutusu — bizim tarafımızda (FİRMAMIZ) firma adı otomatik gelir
    doc.setFontSize(9);
    doc.rect(14, finalY + 12, 82, 28);
    doc.setFont(undefined, "bold");
    doc.text("FİRMAMIZ", 16, finalY + 17);
    doc.setFont(undefined, "normal"); doc.setFontSize(8);
    // Uzun ünvanı satır satır sığdır
    const firmLines = doc.splitTextToSize(c.name || "", 78);
    firmLines.slice(0, 3).forEach((ln, i) => {
      doc.text(ln, 16, finalY + 23 + i * 3.6);
    });
    doc.setFontSize(9); doc.setTextColor(120);
    doc.text("Kaşe / İmza", 16, finalY + 37);
    doc.setTextColor(0);

    doc.rect(110, finalY + 12, 82, 28);
    doc.setFont(undefined, "bold");
    doc.text("CARİ (MUTABAKAT ONAYI)", 112, finalY + 17);
    doc.setFont(undefined, "normal"); doc.setFontSize(8);
    const cariLines = doc.splitTextToSize(contact.name || "", 78);
    cariLines.slice(0, 3).forEach((ln, i) => {
      doc.text(ln, 112, finalY + 23 + i * 3.6);
    });
    doc.setFontSize(9); doc.setTextColor(120);
    doc.text("Kaşe / İmza / Tarih", 112, finalY + 37);
    doc.setTextColor(0);

    // Footer
    doc.setFontSize(8); doc.setTextColor(120);
    doc.text(`Plasper Finans · ${data.reference_no}`, 14, doc.internal.pageSize.getHeight() - 8);

    const safe = contact.name.replace(/[^a-zA-Z0-9-_]/g, "_");
    doc.save(`mutabakat_${safe}_${data.as_of}.pdf`);
    toast.success("PDF indirildi");
  };

  const printForm = () => {
    if (!printableRef.current) return;
    const w = window.open("", "_blank", "width=900,height=1200");
    const html = `
      <html><head><title>Mutabakat · ${contact.name}</title>
      <style>
        body{font-family:-apple-system,Arial,sans-serif;padding:24px;color:#111}
        h1{font-size:20px;margin:0 0 8px;letter-spacing:-0.02em}
        h3{font-size:12px;text-transform:uppercase;letter-spacing:0.14em;color:#555;margin:20px 0 8px}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        td,th{border:1px solid #000;padding:6px 8px;text-align:left;font-size:12px}
        th{background:#000;color:#fff}
        .totals td{background:#f7f7f7;font-weight:600}
        .sign{display:flex;gap:16px;margin-top:24px}
        .sign > div{flex:1;border:1px solid #000;padding:12px;min-height:100px;font-size:11px}
        .muted{color:#666;font-size:11px}
        .row{display:flex;justify-content:space-between;gap:16px}
        .col{flex:1}
        .logo{display:flex;align-items:center;gap:12px;margin-bottom:12px}
        .logo img{width:44px;height:44px;object-fit:contain}
        .position{border:1px solid #000;padding:8px 12px;margin-top:8px;font-weight:600;font-size:12px}
      </style></head><body>
      ${printableRef.current.innerHTML}
      </body></html>`;
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="reconciliation-modal"
        className="bg-white border border-black w-full max-w-4xl my-8"
      >
        {/* Modal header */}
        <div className="flex items-center justify-between p-5 border-b-2 border-black sticky top-0 bg-white z-10">
          <div>
            <div className="label-caps">Cari Mutabakat</div>
            <h2 className="font-display text-xl font-bold">{contact.name}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="reconciliation-settings" onClick={() => setShowSettings(true)}
                    className="p-2 border border-black/20 hover:bg-neutral-100" title="Firma bilgileri">
              <Settings size={15} />
            </button>
            <button data-testid="reconciliation-print" onClick={printForm}
                    className="brutal-btn bg-white border border-black px-3 py-2 text-sm flex items-center gap-2">
              <Printer size={14} /> Yazdır
            </button>
            <button data-testid="reconciliation-export-excel" onClick={exportExcel}
                    className="brutal-btn bg-white border border-black px-3 py-2 text-sm flex items-center gap-2">
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button data-testid="reconciliation-export-pdf" onClick={exportPDF}
                    className="brutal-btn bg-black text-white px-3 py-2 text-sm flex items-center gap-2">
              <Download size={14} /> PDF
            </button>
            <button onClick={onClose} className="p-2 hover:bg-neutral-100"><X size={18} /></button>
          </div>
        </div>

        {/* Controls */}
        <div className="p-5 border-b border-black/10 flex flex-wrap items-end gap-3 bg-neutral-50">
          <label className="text-sm">
            <div className="label-caps mb-1">Mutabakat Tarihi</div>
            <input type="date" data-testid="reconciliation-as-of"
                   value={asOf} onChange={(e) => setAsOf(e.target.value)}
                   className="border border-black/20 px-3 py-1.5 bg-white outline-none" />
          </label>
          <label className="text-sm">
            <div className="label-caps mb-1">Dönem Başlangıcı (Opsiyonel)</div>
            <input type="date" data-testid="reconciliation-period-start"
                   value={start} onChange={(e) => setStart(e.target.value)}
                   className="border border-black/20 px-3 py-1.5 bg-white outline-none" />
          </label>
          <button data-testid="reconciliation-apply" onClick={load}
                  className="brutal-btn bg-black text-white px-4 py-1.5 text-sm">
            Yenile
          </button>
          {loading && <div className="label-caps">Yükleniyor…</div>}
          <div className="ml-auto text-[11px] text-neutral-500 max-w-sm">
            Dönem başlangıcı seçmezseniz mutabakat tarihi itibariyle toplam bakiye gösterilir.
          </div>
        </div>

        {/* Printable form preview */}
        {data && (
          <div ref={printableRef} className="p-8 bg-white">
            <div className="logo flex items-center gap-3 mb-4">
              <img src="/plasper-logo.png" alt="Plasper" className="w-11 h-11 object-contain" />
              <div>
                <h1 className="font-display text-2xl font-bold tracking-tight m-0">CARİ MUTABAKAT FORMU</h1>
                <div className="text-xs text-neutral-500 mt-0.5">
                  Referans No: <span className="font-mono-num">{data.reference_no}</span> · Tarih: {fmtDate(data.as_of)}
                </div>
              </div>
            </div>

            <div className="row grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <PartyBlock title="FİRMAMIZ" party={data.company} />
              <PartyBlock title="CARİ" party={contact} />
            </div>

            <h3 className="label-caps mt-6 mb-2">Kayıtlarımıza Göre Bakiye Özeti</h3>
            <table className="w-full text-sm border border-black">
              <tbody>
                <SummaryRow label="Dönem Başı Bakiye" value={data.opening_balance} />
                <SummaryRow label="Toplam Borç (+)" value={data.total_income} tone="rose" />
                <SummaryRow label="Toplam Alacak (−)" value={data.total_expense} tone="emerald" />
                {!!data.open_invoice_receivable && (
                  <SummaryRow
                    label={`Açık Satış Faturaları — Borç (+) · ${data.open_invoice_count} adet`}
                    value={data.open_invoice_receivable} tone="rose"
                  />
                )}
                {data.open_invoice_receivable_remaining != null &&
                  data.open_invoice_receivable_remaining !== data.open_invoice_receivable && (
                  <SummaryRow
                    label="  Kalan (kısmi ödemeler düşülmüş)"
                    value={data.open_invoice_receivable_remaining} tone="rose"
                  />
                )}
                {!!data.open_invoice_payable && (
                  <SummaryRow
                    label="Açık Alış Faturaları — Alacak (−)"
                    value={data.open_invoice_payable} tone="emerald"
                  />
                )}
                {data.open_invoice_payable_remaining != null &&
                  data.open_invoice_payable_remaining !== data.open_invoice_payable && (
                  <SummaryRow
                    label="  Kalan (kısmi ödemeler düşülmüş)"
                    value={data.open_invoice_payable_remaining} tone="emerald"
                  />
                )}
                {!!data.open_cheque_receivable && (
                  <SummaryRow
                    label={`Portföydeki Alınan Çekler — Alacak (−) · ${data.open_cheque_count || 0} adet`}
                    value={data.open_cheque_receivable} tone="emerald"
                  />
                )}
                {!!data.open_cheque_payable && (
                  <SummaryRow
                    label="Portföydeki Verilen Çekler — Borç (+)"
                    value={data.open_cheque_payable} tone="rose"
                  />
                )}
                {!!data.open_cheque_endorsed_in && (
                  <SummaryRow
                    label={`Ciro ile Ödenen Çekler — Borç (+) · ${data.open_cheque_endorsed_in_count || 0} adet`}
                    value={data.open_cheque_endorsed_in} tone="rose"
                  />
                )}
                <tr className="totals bg-neutral-100">
                  <td className="border border-black p-2 font-semibold">
                    {fmtDate(data.as_of)} İtibariyle Bakiye
                  </td>
                  <td className={`border border-black p-2 text-right font-mono-num font-bold text-base ${
                    data.closing_balance > 0 ? "text-emerald-700" : data.closing_balance < 0 ? "text-rose-600" : ""
                  }`}>
                    {fmtTRY(data.closing_balance)}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className={`position mt-3 border p-3 ${positionLabel.tone}`}>
              {positionLabel.text}
            </div>

            {data.transactions.length > 0 && (
              <>
                <h3 className="label-caps mt-6 mb-2">Hareketler ({data.transactions.length})</h3>
                <table className="w-full text-xs border border-black">
                  <thead>
                    <tr>
                      <th className="border border-black p-2 text-left">Tarih</th>
                      <th className="border border-black p-2 text-left">Açıklama</th>
                      <th className="border border-black p-2 text-left">Kategori</th>
                      <th className="border border-black p-2 text-right">Borç (+)</th>
                      <th className="border border-black p-2 text-right">Alacak (−)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((t, i) => (
                      <tr key={i}>
                        <td className="border border-black p-2 font-mono-num">{t.date}</td>
                        <td className="border border-black p-2">{t.description || "—"}</td>
                        <td className="border border-black p-2">{t.category || "—"}</td>
                        <td className="border border-black p-2 text-right font-mono-num text-emerald-700">
                          {t.income ? fmtTRY(t.income) : "—"}
                        </td>
                        <td className="border border-black p-2 text-right font-mono-num text-rose-600">
                          {t.expense ? fmtTRY(t.expense) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <p className="mt-6 text-sm">
              Yukarıda yer alan bakiye kayıtlarınızla mutabık ise formu <b>kaşe ve imzalayarak</b> tarafımıza gönderiniz.
              Mutabık değilseniz farkları belirten yanıtınızı beklemekteyiz.
            </p>

            <div className="sign grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
              <div className="border border-black p-4 min-h-[110px]">
                <div className="label-caps">FİRMAMIZ</div>
                <div className="text-sm mt-1 font-medium">{data.company.name}</div>
                <div className="text-[11px] text-neutral-500 mt-1">Kaşe / İmza</div>
              </div>
              <div className="border border-black p-4 min-h-[110px]">
                <div className="label-caps">CARİ (MUTABAKAT ONAYI)</div>
                <div className="text-sm mt-1 font-medium">{contact.name}</div>
                <div className="text-[11px] text-neutral-500 mt-1">Kaşe / İmza / Tarih</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showSettings && (
        <CompanySettingsModal onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); load(); }} />
      )}
    </div>
  );
}

function PartyBlock({ title, party }) {
  const lines = [
    party.name,
    party.tax_office && `V.D.: ${party.tax_office}`,
    party.tax_id && `VKN/TCKN: ${party.tax_id}`,
    party.address,
    party.phone,
    party.email,
  ].filter(Boolean);
  return (
    <div className="col border border-black p-4">
      <div className="label-caps">{title}</div>
      <div className="mt-2 space-y-0.5 text-sm">
        {lines.length === 0 ? <span className="text-neutral-500 text-xs">Bilgi girilmedi.</span> :
          lines.map((l, i) => <div key={i} className={i === 0 ? "font-semibold" : "text-neutral-700"}>{l}</div>)
        }
      </div>
    </div>
  );
}

function SummaryRow({ label, value, tone }) {
  const color = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : "";
  return (
    <tr>
      <td className="border border-black p-2">{label}</td>
      <td className={`border border-black p-2 text-right font-mono-num ${color}`}>{fmtTRY(value)}</td>
    </tr>
  );
}

function CompanySettingsModal({ onClose, onSaved }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get("/settings/company").then((r) => setForm(r.data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings/company", form);
      toast.success("Firma bilgileri kaydedildi");
      onSaved();
    } catch { toast.error("Kaydedilemedi"); }
  };

  if (!form) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            data-testid="company-settings-modal"
            className="bg-white border border-black w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-lg font-bold">Firma Bilgileri</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <F label="Ünvan">
            <input required data-testid="company-name" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Vergi Dairesi">
              <input data-testid="company-tax-office" value={form.tax_office}
                     onChange={(e) => setForm({ ...form, tax_office: e.target.value })} className="input" />
            </F>
            <F label="VKN">
              <input data-testid="company-tax-id" value={form.tax_id}
                     onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="input font-mono-num" />
            </F>
          </div>
          <F label="Adres">
            <textarea rows={2} data-testid="company-address" value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })} className="input" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Telefon">
              <input data-testid="company-phone" value={form.phone}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
            </F>
            <F label="E-posta">
              <input data-testid="company-email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" />
            </F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Banka">
              <input data-testid="company-bank" value={form.bank}
                     onChange={(e) => setForm({ ...form, bank: e.target.value })} className="input" />
            </F>
            <F label="IBAN">
              <input data-testid="company-iban" value={form.iban}
                     onChange={(e) => setForm({ ...form, iban: e.target.value })} className="input font-mono-num" />
            </F>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="company-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">
            Kaydet
          </button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>;
}
